// Sanitization helpers for untrusted text at system boundaries.
// Used by the backend before storing or forwarding text to the LLM, and by
// frontends to validate URLs before rendering links from LLM output.
// Matches ASCII control chars (U+0000–U+001F except \t \n \r) and DEL (U+007F),
// plus zero-width chars (U+200B–U+200F), bidi overrides (U+202A–U+202E),
// format chars (U+2060–U+206F) and BOM (U+FEFF).
// Built programmatically so source files stay free of literal control bytes.
function buildControlCharRegex() {
    const ranges = [
        [0x00, 0x08],
        [0x0b, 0x0c],
        [0x0e, 0x1f],
        [0x7f, 0x7f],
        [0x200b, 0x200f],
        [0x202a, 0x202e],
        [0x2060, 0x206f],
        [0xfeff, 0xfeff],
    ];
    const escape = (n) => `\\u${n.toString(16).padStart(4, "0")}`;
    const parts = ranges.map(([lo, hi]) => lo === hi ? escape(lo) : `${escape(lo)}-${escape(hi)}`);
    return new RegExp(`[${parts.join("")}]`);
}
export const CONTROL_CHAR_REGEX = buildControlCharRegex();
// Global twin for replacing. Kept separate on purpose: a `g` regex carries
// `lastIndex` between `.test()` calls, which would make `hasControlChars`
// answer differently on alternate calls.
const CONTROL_CHAR_REGEX_GLOBAL = new RegExp(CONTROL_CHAR_REGEX.source, "g");
// Vertical tab is Word's Shift+Enter line break and form feed its page break:
// both mean "new line" to the person who typed them.
const LINE_BREAK_CONTROL_CHARS = /[\u000b\u000c]/g;
const RESERVED_XML_TAGS = [
    "</knowledge_base>",
    "</agent_context>",
    "</security_rules>",
    "</user_message>",
];
// Hard cap that matches the widget Zod schema. WhatsApp can deliver longer
// payloads, so we still accept and truncate rather than reject.
const MAX_USER_MESSAGE_LEN = 2000;
export function hasControlChars(text) {
    return CONTROL_CHAR_REGEX.test(text);
}
/**
 * Removes every character `hasControlChars` rejects, turning Word's line and
 * page breaks into newlines. For text a person pasted into an editor (Word and
 * Google Docs carry zero-width and bidi marks they cannot see), so it is
 * cleaned instead of refused. Not a substitute for the server-side check.
 */
export function stripControlChars(text) {
    return text
        .replace(LINE_BREAK_CONTROL_CHARS, "\n")
        .replace(CONTROL_CHAR_REGEX_GLOBAL, "");
}
export function hasReservedXmlTag(text) {
    return RESERVED_XML_TAGS.some((tag) => text.includes(tag));
}
/**
 * Neutralizes literal closing tags so untrusted content cannot escape the XML block it
 * is injected into and start issuing sibling instructions to the model.
 *
 * Lives here rather than in the service that first needed it because three prompt paths
 * now depend on it — the user message, the knowledge base, and the material an operator
 * pastes about a client during onboarding — and a second copy of this is a security bug
 * waiting for someone to fix only one of them.
 */
export function escapeForXmlBlock(content, tag) {
    const closeTag = `</${tag}>`;
    return content.split(closeTag).join(`</${tag} >`);
}
// Returns the cleaned message, or null if the input is unusable (wrong type
// or empty). Callers MUST treat null as "drop the message silently."
export function cleanUserMessage(raw) {
    if (typeof raw !== "string")
        return null;
    const trimmed = raw.trim();
    if (trimmed.length === 0)
        return null;
    const normalized = trimmed.normalize("NFKC");
    if (hasControlChars(normalized))
        return null;
    return normalized.slice(0, MAX_USER_MESSAGE_LEN);
}
const ALLOWED_URL_SCHEMES = new Set([
    "http:",
    "https:",
    "mailto:",
    "tel:",
    "whatsapp:",
]);
// Returns the original href if its scheme is in the allowlist, otherwise
// undefined. Callers should treat undefined as "render as plain text, not a
// link" to neutralize javascript:/data:/vbscript: URLs from LLM output.
export function safeHref(href) {
    if (!href)
        return undefined;
    try {
        const url = new URL(href, "https://placeholder.local");
        if (ALLOWED_URL_SCHEMES.has(url.protocol))
            return href;
    }
    catch {
        return undefined;
    }
    return undefined;
}
//# sourceMappingURL=sanitize.js.map